/**
 * Génère l'URL de suivi (tracking) selon le transporteur
 */

/**
 * Génère l'URL de tracking selon l'idLivraison
 * @param trackingNumber - Numéro de suivi
 * @param idLivraison - ID de la livraison
 * @param langue - Langue (fr, en, es, it) - par défaut 'fr'
 * @returns URL de tracking ou null si pas de tracking disponible
 */
export function getTrackingUrl(
  trackingNumber: string,
  idLivraison: number | null | undefined,
  langue = 'fr'
): string | null {
  const number = trackingNumber?.trim()
  if (!number) {
    return null
  }

  // Normaliser la langue (uk -> en)
  if (langue === 'uk') langue = 'en'

  if (!idLivraison) {
    // Easy Chronopost return labels often look like XX123456789JB.
    if (/^XX\d+JB$/i.test(number)) {
      return `https://www.chronopost.fr/tracking-no-cms/suivi-page?langue=fr_FR&listeNumerosLT=${encodeURIComponent(number)}`
    }
    return null
  }

  // DPD (idLivraison = 10, 13, 2743)
  if (idLivraison === 10 || idLivraison === 13 || idLivraison === 2743) {
    return `http://www.dpd.fr/traces_092${number}`
  }

  // UPS (idLivraison = 19, 2744, 2745, 2923)
  if ([19, 2744, 2745, 2923].includes(idLivraison)) {
    const localeMap: Record<string, string> = {
      fr: 'fr_FR',
      en: 'en_US',
      es: 'es_ES',
      it: 'it_IT'
    }
    const locale = localeMap[langue] || 'en_US'
    return `https://www.ups.com/track?requester=QUIC&loc=${locale}&tracknum=${number}`
  }

  // Colissimo / La Poste (idLivraison = 1, 4, 8, 12, 16, 18, 2736, 2737, 2746, 2924, 2925)
  if ([1, 4, 8, 12, 16, 18, 2736, 2737, 2746, 2924, 2925].includes(idLivraison)) {
    const urls: Record<string, string> = {
      fr: 'https://www.laposte.fr/outils/suivre-vos-envois?code=',
      en: 'https://www.laposte.fr/outils/track-a-parcel?code='
    }
    const url = urls[langue] || urls['fr']
    return `${url}${number}`
  }

  // GLS (idLivraison = 14, 15, 2742, 2931)
  if ([14, 15, 2742, 2931].includes(idLivraison)) {
    const urls: Record<string, string> = {
      fr: 'https://gls-group.com/FR/fr/suivi-colis?match=',
      en: 'https://gls-group.com/FR/en/parcel-tracking?match=',
      es: 'https://www.gls-spain.es/es/recibir-paquetes/seguimiento-envio/?international=1&match=',
      it: 'https://gls-group.com/IT/it/servizi-online/ricerca-spedizioni.html?type=INT&match='
    }
    const url = urls[langue] || urls['en']
    return url ? `${url}${number}` : null
  }

  // Chronopost (idLivraison = 2729, 2730, 2731, 2732, 2747, 2917, 2918, 2919, 2920, 2921, 2922)
  if ([2729, 2730, 2731, 2732, 2747, 2917, 2918, 2919, 2920, 2921, 2922].includes(idLivraison)) {
    const urls: Record<string, string> = {
      fr: 'https://www.chronopost.fr/tracking-no-cms/suivi-page?langue=fr_FR&listeNumerosLT=',
      en: 'https://www.chronopost.fr/tracking-no-cms/suivi-page?langue=en_EN&listeNumerosLT=',
      es: 'https://www.chronopost.fr/tracking-no-cms/suivi-page?langue=es_ES&listeNumerosLT=',
      it: 'https://www.chronopost.fr/tracking-no-cms/suivi-page?langue=it_IT&listeNumerosLT='
    }
    const url = urls[langue] || urls['fr']
    return url ? `${url}${number}` : null
  }

  // DHL (idLivraison = 2733, 2734, 2735, 2738, 2739)
  if ([2733, 2734, 2735, 2738, 2739].includes(idLivraison)) {
    const urls: Record<string, string> = {
      fr: 'https://www.dhl.com/fr-fr/home/tracking/tracking-express.html?submit=1&tracking-id=',
      en: 'https://www.dhl.com/gb-en/home/tracking/tracking-express.html?submit=1&tracking-id=',
      es: 'https://www.dhl.com/es-es/home/tracking/tracking-express.html?submit=1&tracking-id=',
      it: 'https://www.dhl.com/it-it/home/tracking/tracking-express.html?submit=1&tracking-id='
    }
    const url = urls[langue] || urls['en']
    return url ? `${url}${number}` : null
  }

  // FEDEX (idLivraison = 2748, 2926, 2927, 2928, 2929, 2930)
  if ([2748, 2926, 2927, 2928, 2929, 2930].includes(idLivraison)) {
    return `https://www.fedex.com/fedextrack/?trknbr=${number}`
  }

  // GPX (idLivraison = 6, 11, 2649, 2915)
  if ([6, 11, 2649, 2915].includes(idLivraison)) {
    return `https://odoo.gpxlogistique.com/tracking?order_number=${number}`
  }

  // BOLLORE (idLivraison = 2916)
  if (idLivraison === 2916) {
    return 'https://link.cevalogistics.com'
  }

  // Esendeo direct (idLivraison = 2740)
  // TODO: Récupérer config.esendeo_suivi depuis la DB
  if (idLivraison === 2740) {
    return null
  }

  return null
}
